import type { Player } from '../interfaces/index.js';

interface PlayerEconomy {
  playerId: string;
  gold: number;
  incomePerSec: number;
  baseLevel: number;
  goldAccum: number;
}

export interface GoldTickResult {
  playerId: string;
  goldAdded: number;
  newTotal: number;
  newIncome: number;
}

export class EconomySystem {
  private economies: Map<string, PlayerEconomy>;
  private upgradeBaseCosts: Record<number, number>;

  constructor(players: Player[], upgradeBaseCosts: Record<number, number> = { 1: 600, 2: 1500 }) {
    this.economies = new Map();
    this.upgradeBaseCosts = upgradeBaseCosts;
    for (const player of players) {
      this.economies.set(player.id, {
        playerId: player.id,
        gold: player.gold,
        incomePerSec: player.incomePerSec,
        baseLevel: player.baseLvl,
        goldAccum: 0,
      });
    }
  }

  tick(deltaMs: number): GoldTickResult[] {
    // Pre-size to avoid repeated push() allocations in the common 2-player case.
    const results: GoldTickResult[] = new Array(this.economies.size);
    let i = 0;
    for (const econ of this.economies.values()) {
      const earned = econ.incomePerSec * (deltaMs / 1000);
      econ.goldAccum += earned;
      const floored = Math.floor(econ.goldAccum);
      econ.goldAccum -= floored;
      econ.gold += floored;
      results[i++] = {
        playerId: econ.playerId,
        goldAdded: floored,
        newTotal: econ.gold,
        newIncome: econ.incomePerSec,
      };
    }
    return results;
  }

  spend(playerId: string, amount: number): boolean {
    const econ = this.economies.get(playerId);
    if (econ === undefined) return false;
    if (econ.gold < amount) return false;
    econ.gold -= amount;
    if (econ.gold < 0) econ.gold = 0;
    return true;
  }

  addGold(playerId: string, amount: number): void {
    const econ = this.economies.get(playerId);
    if (!econ) return;
    econ.gold = Math.max(0, econ.gold + amount);
  }

  addIncome(playerId: string, incomePerSec: number): void {
    const econ = this.economies.get(playerId);
    if (econ === undefined) return;
    econ.incomePerSec += incomePerSec;
  }

  getGold(playerId: string): number {
    return this.economies.get(playerId)?.gold ?? 0;
  }

  getIncome(playerId: string): number {
    return this.economies.get(playerId)?.incomePerSec ?? 0;
  }

  getBaseLevel(playerId: string): number {
    return this.economies.get(playerId)?.baseLevel ?? 1;
  }

  upgradeBase(playerId: string): boolean {
    const econ = this.economies.get(playerId);
    if (econ === undefined) return false;
    if (econ.baseLevel >= 3) return false;
    const cost = this.upgradeBaseCosts[econ.baseLevel];
    if (cost === undefined) return false;
    if (econ.gold < cost) return false;
    econ.gold = Math.max(0, econ.gold - cost);
    econ.baseLevel += 1;
    return true;
  }

  getSnapshot(): PlayerEconomy[] {
    return Array.from(this.economies.values()).map((e) => ({ ...e }));
  }
}
